/**
 * MarketplacePage
 *
 * Landing page for marketplace - shows expert coaches and community pitches.
 * Includes ForHireCard components for available experts.
 */

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { useSearchMarketplaceProfilesQuery, MarketplaceProfile } from '../store/api/marketplaceApi';
import { ForHireCard, MarketplacePerson, AuthorityLevelKey } from '../components/marketplace/ForHireCard';
import { useCentralConsumer } from '../hooks/useCentralConsumer';
import { useUsageAllowance } from '../hooks/useUsageAllowance';
import { allowanceText, isKnownExhausted } from '../utils/billingStatusView';

const DOMAIN_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'sales', label: 'Sales' },
  { value: 'coaching', label: 'Coaching' },
  { value: 'leadership', label: 'Leadership' },
  { value: 'tech', label: 'Tech' },
];

// Helper to transform MarketplaceProfile (flat backend shape) to MarketplacePerson
export function transformToMarketplacePerson(profile: MarketplaceProfile): MarketplacePerson {
  return {
    userId: profile.userId,
    displayName: profile.displayName || '',
    headline: profile.headline || undefined,
    avatarUrl: undefined,
    publicSlug: profile.publicSlug || undefined,
    // Contact (a hire request) names this portfolio; it was hard-coded
    // undefined, so every Contact attempt failed before any request.
    portfolioId: profile.portfolioId || undefined,
    domains: [...(profile.verifiedRoles || []), ...(profile.verifiedSpecialties || [])],
    badges: profile.badges || [],
    hourlyRate: (profile.rateType === 'hour' || profile.rateType === 'hourly') ? profile.rateAmount : null,
    sessionRate: profile.rateType === 'session' ? profile.rateAmount : null,
    currency: profile.currency || 'USD',
    sessionsCompleted: profile.sessionCount ?? undefined,
    averageRating: null,
    isCoach: profile.verifiedRoles?.includes('coach') || false,
    isAvailableForHire: profile.availableForHire || false,
    location: profile.location || undefined,
    overallScore: profile.overallScore ?? null,
    authorityLevel: profile.authorityLevel as AuthorityLevelKey | undefined,
  };
}

export function MarketplacePage() {
  const [domainFilter, setDomainFilter] = useState('all');
  const [coachesOnly, setCoachesOnly] = useState(false);

  const { hasCapability, bootCompleted } = useCentralConsumer();
  const isPaid = hasCapability('marketplace', 'listen');
  const isFree = !isPaid;
  const isPlanLoading = !bootCompleted;
  // Display only: unknown is 'Unavailable', and the server decides each listen.
  const { listen: listenAllowance } = useUsageAllowance({ skip: !isPaid });
  const listenText = allowanceText(listenAllowance);

  // Fetch marketplace profiles
  const { data: marketplaceData, isLoading: isLoadingProfiles } = useSearchMarketplaceProfilesQuery({
    limit: 6,
    sortBy: 'rankingScore',
    sortOrder: 'DESC',
  }, { refetchOnMountOrArgChange: true });

  // Transform and filter marketplace profiles
  const experts = useMemo<MarketplacePerson[]>(() => {
    if (!marketplaceData?.profiles) return [];

    return marketplaceData.profiles
      .filter((p) => p.availableForHire)
      .map((profile) => transformToMarketplacePerson(profile))
      .filter((person) => {
        // Apply domain filter
        if (domainFilter !== 'all') {
          const matchesDomain = person.domains.some((d) =>
            d.toLowerCase().includes(domainFilter.toLowerCase())
          );
          if (!matchesDomain) return false;
        }
        // Apply coaches filter
        if (coachesOnly && !person.isCoach) return false;
        return true;
      });
  }, [marketplaceData, domainFilter, coachesOnly]);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marketplace</h1>
          <p className="mt-1 text-gray-500">
            Discover top performers and hear how they handle real conversations
          </p>
        </div>
        {isPaid && !isPlanLoading && listenText && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-primary-50 rounded-lg" data-testid="listen-allowance">
            <CreditIcon className="w-4 h-4 text-primary-600" />
            <span className="text-sm font-medium text-primary-700">
              Full listens: {listenText}
            </span>
          </div>
        )}
      </div>

      {/* Upgrade nudge for free users */}
      {isFree && !isPlanLoading && (
        <div className="bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-200 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <LockIcon className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Unlock Full Marketplace Access</h3>
                <p className="text-sm text-gray-600 mt-0.5">
                  Listen to full recordings, review real performance examples, and connect with experts.
                </p>
              </div>
            </div>
            <Link
              to="/settings/billing"
              className="flex-shrink-0 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors text-center"
            >
              Manage Plan
            </Link>
          </div>
        </div>
      )}

      {/* Allowance used up — shown only when the server KNOWS it is (never on unknown) */}
      {isPaid && isKnownExhausted(listenAllowance) && !isPlanLoading && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              <WarningTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-amber-800">Monthly Listening Allowance Used</h3>
                <p className="text-sm text-amber-700 mt-0.5">
                  You've used this month's full-listen allowance. It resets at the start of next month.
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Link
                to="/settings/billing"
                className="px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors"
              >
                Manage Plan
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Featured banner */}
      <div className="card bg-gradient-to-r from-purple-500 to-indigo-600 text-white overflow-hidden">
        <div className="card-body relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative">
            <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm font-medium mb-3">
              Featured
            </span>
            <h2 className="text-2xl font-bold">Practice. Publish what you choose.</h2>
            <p className="text-purple-100 mt-2 max-w-lg">
              Practice real conversations, then choose which results to share on your
              profile so others can see how you handle real scenarios.
            </p>
            <Link
              to="/marketplace/experts"
              className="mt-4 inline-block bg-white text-purple-600 px-6 py-3 rounded-lg font-medium hover:bg-purple-50 transition-colors"
            >
              Learn More
            </Link>
          </div>
        </div>
      </div>

      {/* Main sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Expert Coaches card */}
        <Link
          to="/marketplace/experts"
          className="card hover:shadow-lg transition-shadow group"
        >
          <div className="card-body">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                <UserGroupIcon className="w-7 h-7 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                  Expert Coaches
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Connect with professionals for coaching and feedback.
                </p>
              </div>
              <ArrowRightIcon className="w-5 h-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
            </div>

            {/* Expert count */}
            <div className="flex items-center mt-4 pt-4 border-t">
              <UserGroupIcon className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500 ml-2">
                {marketplaceData?.total != null ? `${marketplaceData.total} experts available` : 'Browse experts'}
              </span>
            </div>
          </div>
        </Link>

        {/* Community Pitches card */}
        <Link
          to="/marketplace/pitches"
          className="card hover:shadow-lg transition-shadow group"
        >
          <div className="card-body">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-200 transition-colors">
                <MicrophoneIcon className="w-7 h-7 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-green-600 transition-colors">
                  Sample Calls
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Hear how top performers handle real conversations and scenarios.
                </p>
              </div>
              <ArrowRightIcon className="w-5 h-5 text-gray-400 group-hover:text-green-600 group-hover:translate-x-1 transition-all" />
            </div>

            {/* Preview stats */}
            <div className="flex items-center gap-6 mt-4 pt-4 border-t">
              <div className="flex items-center gap-2">
                <PlayIcon className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-500">Browse examples</span>
              </div>
            </div>
          </div>
        </Link>
      </div>

      {/* Featured Experts Section */}
      <div className="card">
        <div className="card-header">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="font-semibold text-gray-900">Featured Experts</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Top-performing professionals available for hire
              </p>
            </div>
            <Link
              to="/marketplace/experts"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              View all &rarr;
            </Link>
          </div>
        </div>
        <div className="card-body">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            {/* Domain pills */}
            <div className="flex flex-wrap gap-2">
              {DOMAIN_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => setDomainFilter(filter.value)}
                  className={clsx(
                    'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                    domainFilter === filter.value
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  )}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {/* Coaches only toggle */}
            <label className="flex items-center gap-2 text-sm cursor-pointer ml-auto">
              <input
                type="checkbox"
                checked={coachesOnly}
                onChange={(e) => setCoachesOnly(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-gray-600">Coaches only</span>
            </label>
          </div>

          {/* Experts grid */}
          {isLoadingProfiles ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="card">
                    <div className="card-body">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-gray-200 rounded-full" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-gray-200 rounded w-2/3" />
                          <div className="h-3 bg-gray-200 rounded w-1/2" />
                        </div>
                      </div>
                      <div className="mt-4 space-y-2">
                        <div className="h-3 bg-gray-200 rounded w-1/3" />
                        <div className="h-8 bg-gray-200 rounded" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : experts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {experts.slice(0, 6).map((expert) => (
                <ForHireCard key={expert.userId} person={expert} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <UserGroupIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No experts available yet in this category.</p>
              <p className="text-sm text-gray-400 mt-1">
                Try a different filter or check back later.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Popular categories */}
      <div className="card">
        <div className="card-header">
          <h2 className="font-semibold text-gray-900">Popular Categories</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <CategoryCard
              icon={<RocketIcon className="w-5 h-5" />}
              title="Startup Pitches"
            />
            <CategoryCard
              icon={<BriefcaseIcon className="w-5 h-5" />}
              title="Sales"
            />
            <CategoryCard
              icon={<ChatBubbleIcon className="w-5 h-5" />}
              title="Negotiations"
            />
            <CategoryCard
              icon={<PresentationIcon className="w-5 h-5" />}
              title="Public Speaking"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

interface CategoryCardProps {
  icon: React.ReactNode;
  title: string;
}

function CategoryCard({ icon, title }: CategoryCardProps) {
  return (
    <Link
      to="/marketplace/experts"
      className="flex flex-col items-center p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
    >
      <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mb-2">
        {icon}
      </div>
      <span className="text-sm font-medium text-gray-900">{title}</span>
    </Link>
  );
}

// Icon components
function CreditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function UserGroupIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  );
}

function MicrophoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
      />
    </svg>
  );
}

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function RocketIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"
      />
    </svg>
  );
}

function BriefcaseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}

function ChatBubbleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
      />
    </svg>
  );
}

function PresentationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
      />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
      />
    </svg>
  );
}

function WarningTriangleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}
